import styles from "./CancelSessionScreen.module.scss";

import { useCallback, useEffect, useMemo, useState } from "react";

import { useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";

import { useCoachingApi } from "@/modules/coaching/shared/hooks/useCoachingApi";
import { AppButton } from "@/modules/shared/components/AppButton";
import { AppRadioGroup } from "@/modules/shared/components/AppRadioGroup";
import { PageContainer } from "@/modules/shared/components/PageContainer";
import { useRefetchOnTabFocus } from "@/modules/shared/hooks/useRefetchOnTabFocus";
import {
  ShortUrlFlow,
  ShortUrlTerminalState,
} from "@/modules/tracking/consts/ShortUrl";
import { usePageLoaded } from "@/modules/tracking/hooks/usePageLoaded";
import { useShortUrlFlowAbandonment } from "@/modules/tracking/hooks/useShortUrlFlowAbandonment";
import { completeShortUrlFlow } from "@/modules/tracking/utils/completeShortUrlFlow";
import { Route } from "@/routes/$sessionCode/cancel/$cancelUuid";

import { EndedSession } from "../../components/EndedSession/EndedSession";
import { LateJoinSession } from "../../components/LateJoinSession";
import { MissedSession } from "../../components/MissedSession";
import { NevermindCancel } from "../../components/NevermindCancel";
import { CANCELLATION_REASONS } from "../../consts/cancellationReasons";
import { OTHER_REASON_CHAR_LIMIT } from "../../consts/otherReasonCharLimit";
import { SESSION_STATUS } from "../../consts/sessionStatus";
import { CancellationReason } from "../../types/CancellationReason";
import { SessionStatusBasedOnTime } from "../../types/SessionStatusBasedOnTime";
import { sessionStatusBasedOnTime } from "../../utils/sessionStatusBasedOnTime";

type SessionStatusState = SessionStatusBasedOnTime | "";

export const CancelSessionScreen = () => {
  useRefetchOnTabFocus();
  useShortUrlFlowAbandonment();

  const navigate = useNavigate();
  const { t } = useTranslation();
  const { rescheduleUuid } = Route.useSearch();
  const { sessionCode, cancelUuid } = Route.useParams();
  const {
    data: { isLateCancel, session },
  } = Route.useLoaderData();
  const { confirmSessionCancel } = useCoachingApi();

  const [isLoading, setIsLoading] = useState(false);
  const [selectedValue, setSelectedValue] = useState<string>("");
  const [reasonForCancellation, setReasonForCancellation] = useState("");
  const [error, setError] = useState<CancelFormValidationErrors | null>(null);
  const [nevermindPressed, setNevermindPressed] = useState(false);
  const [sessionStatus, setSessionStatus] = useState<SessionStatusState>("");

  usePageLoaded({
    flow: ShortUrlFlow.Cancel,
    session_id: String(session.id),
    coach_id: String(session.coachId),
    time_until_session_start_minutes: null,
    is_first_visit: true,
    has_joined_previously: false,
  });

  useEffect(() => {
    const sessionStatus = sessionStatusBasedOnTime(session.start);

    // eslint-disable-next-line react-hooks/set-state-in-effect
    setSessionStatus(sessionStatus);
  }, [session.start]);

  const isOtherSelected = useMemo(
    () => selectedValue === CANCELLATION_REASONS.OTHER,
    [selectedValue],
  );

  const options = [
    {
      id: "0",
      label: t("cancelSession.reasons.scheduleDifferentDate"),
      value: CANCELLATION_REASONS.RESCHEDULE,
    },
    {
      id: "1",
      label: t("cancelSession.reasons.notReadyForSession"),
      value: CANCELLATION_REASONS.NOT_READY,
    },
    {
      id: "2",
      label: t("cancelSession.reasons.notContinueWithCoach"),
      value: CANCELLATION_REASONS.DIFFERENT_COACH,
    },
    {
      id: "3",
      label: t("cancelSession.reasons.other"),
      value: CANCELLATION_REASONS.OTHER,
    },
  ];

  const handleChangeReasonForCancellation = (
    e: React.ChangeEvent<HTMLTextAreaElement>,
  ) => {
    const inputValue = e.target.value;
    setReasonForCancellation(() => {
      if (inputValue.length <= OTHER_REASON_CHAR_LIMIT) {
        setError(null);
      }

      return inputValue;
    });
  };

  const handleCancelPress = useCallback(async () => {
    if (!selectedValue) {
      setError("noOptionSelected");
      return;
    }

    if (isOtherSelected && !reasonForCancellation) {
      setError("reasonRequired");
      return;
    }

    if (reasonForCancellation.length > OTHER_REASON_CHAR_LIMIT) {
      setError("reasonTooLong");
      return;
    }

    setError(null);

    try {
      setIsLoading(true);

      await confirmSessionCancel({
        uuid: cancelUuid,
        reason: selectedValue as CancellationReason,
        otherReason: isOtherSelected ? reasonForCancellation : null,
      });

      completeShortUrlFlow(ShortUrlTerminalState.CancellationSubmitted, {
        extraProps: { coach_id: session.coachId ?? null },
      });

      navigate({
        to: `/${sessionCode}/cancel/${cancelUuid}/success`,
        search: () => ({ rescheduleUuid }),
      });
    } catch (error) {
      if (error instanceof Error) {
        alert(error.message);
      }
    } finally {
      setIsLoading(false);
    }
  }, [
    confirmSessionCancel,
    navigate,
    reasonForCancellation,
    selectedValue,
    sessionCode,
    isOtherSelected,
    cancelUuid,
    rescheduleUuid,
    session.coachId,
  ]);

  const handleNevermindPress = () => {
    setNevermindPressed(true);
  };

  const handleOnCancelPress = () => setSessionStatus("");

  const handleReschedulePress = () => {
    if (!rescheduleUuid) {
      return;
    }

    navigate({
      to: `/${sessionCode}/reschedule/${rescheduleUuid}`,
    });
  };

  const handleJoinPress = () => {
    navigate({ to: `/${sessionCode}` });
  };

  if (sessionStatus === SESSION_STATUS.LATE_JOIN) {
    return (
      <LateJoinSession
        handleCancel={handleOnCancelPress}
        handleJoin={handleJoinPress}
        handleReschedule={handleReschedulePress}
      />
    );
  }

  if (sessionStatus === SESSION_STATUS.MISSED) {
    return (
      <MissedSession
        handleCancel={handleOnCancelPress}
        handleReschedule={handleReschedulePress}
      />
    );
  }

  if (sessionStatus === SESSION_STATUS.ENDED) {
    return <EndedSession handleReschedule={handleReschedulePress} />;
  }

  if (sessionStatus === SESSION_STATUS.EXPIRED) {
    navigate({ to: `/not-found` });
  }

  if (nevermindPressed) {
    return <NevermindCancel />;
  }

  return (
    <PageContainer variant="constrained">
      <div className={styles.container}>
        <span className={styles.heading}>{t("cancelSession.title")}</span>
        <span className={styles.subheader}>
          {isLateCancel
            ? t("cancelSession.lateCancelDescription")
            : t("cancelSession.description")}
        </span>
        <AppRadioGroup
          options={options}
          selectedValue={selectedValue || ""}
          onChange={setSelectedValue}
        />
        {isOtherSelected && (
          <div className={styles.textAreaWrapper}>
            <span className={styles.cancellationReasonHeading}>
              {t("cancelSession.cancelReason.title")}
            </span>
            <textarea
              placeholder={t("cancelSession.cancelReason.placeholder")}
              value={reasonForCancellation}
              onChange={handleChangeReasonForCancellation}
              className={
                error === "reasonRequired" || error === "reasonTooLong"
                  ? styles.error
                  : ""
              }
            />
            {error === "reasonRequired" && (
              <div className={styles.errorMessage}>
                {t("cancelSession.errors.reasonRequired")}
              </div>
            )}
            {error === "reasonTooLong" && (
              <div className={styles.errorMessage}>
                {t("cancelSession.errors.reasonTooLong")}
              </div>
            )}
            <div className={styles.cancellationReasonLength}>
              {reasonForCancellation.length} / {OTHER_REASON_CHAR_LIMIT}
            </div>
          </div>
        )}

        {error === "noOptionSelected" && (
          <div className={styles.errorMessageContainer}>
            <div className={styles.errorMessage}>
              {t("cancelSession.errors.noOptionSelected")}
            </div>
          </div>
        )}

        <div className={styles.footer}>
          <AppButton
            variant="surface"
            onClick={handleCancelPress}
            isLoading={isLoading}
          >
            <span className={styles.cancelSessionButton}>
              {t("cancelSession.buttons.cancelSession")}
            </span>
          </AppButton>

          <AppButton variant="outlined" onClick={handleNevermindPress}>
            <span>{t("cancelSession.buttons.neverMind")}</span>
          </AppButton>
        </div>
      </div>
    </PageContainer>
  );
};
